import { CustomChipColors } from '#/common/utils/wrappers/withChip';
import { Alert } from '#/graphql/types';
import { Priority as FindingPriority } from '#/modules/findings/utils/constants';
import { Priority as WorkOrderPriority } from '#/modules/workOrder/mappings/mappings';
import { color } from '@nuvolo/nuux-theme/utils';

export const states = {
  new: { value: 'new', text: 'New' },
  ignored: { value: 'ignored', text: 'Ignored' },
  processed: { value: 'processed', text: 'Processed' },
  resolved: { value: 'resolved', text: 'Resolved' },
};

export const statesList = Object.values(states);

export const impacts = {
  none: { value: 'none', text: 'None' },
  low: { value: 'low', text: 'Low' },
  medium: { value: 'medium', text: 'Medium' },
  high: { value: 'high', text: 'High' },
};

export const impactsList = Object.values(impacts);

export const mapAlertImpactToFindingPriority = (alertImpact: string): number => {
  const mapping: Record<string, number> = {
    [impacts.none.text]: FindingPriority.Planning,
    [impacts.low.text]: FindingPriority.Low,
    [impacts.medium.text]: FindingPriority.Moderate,
    [impacts.high.text]: FindingPriority.High,
  };
  return mapping[alertImpact] || FindingPriority.Planning;
};

export const mapAlertImpactToWorkOrderPriority = (alertImpact: string): number => {
  const mapping: Record<string, number> = {
    [impacts.none.text]: WorkOrderPriority.VeryLow,
    [impacts.low.text]: WorkOrderPriority.Low,
    [impacts.medium.text]: WorkOrderPriority.Medium,
    [impacts.high.text]: WorkOrderPriority.High,
  };

  return mapping[alertImpact] || WorkOrderPriority.VeryLow;
};

export const getHighestImpactPriority = (
  impactComfort: string | null | undefined,
  impactEnergy: string | null | undefined,
  impactReliability: string | null | undefined,
  recordType: 'finding' | 'workOrder',
): number => {
  const impactValues = [impactComfort, impactEnergy, impactReliability];
  const impactPriorities = impactValues
    .filter((impact) => impact != null)
    .map((impact) =>
      recordType === 'finding'
        ? mapAlertImpactToFindingPriority(impact!)
        : mapAlertImpactToWorkOrderPriority(impact!),
    );

  return impactPriorities.length > 0
    ? Math.max(...impactPriorities)
    : recordType === 'finding'
      ? FindingPriority.Planning
      : WorkOrderPriority.VeryLow;
};

// Define custom color schemes for alert  datagrid chips
export const alertStateColors: Record<string, CustomChipColors> = {
  new: {
    backgroundColor: color('info', 'dark', 0.15),
    textColor: color('info', 'dark'),
  },
  ignored: {
    backgroundColor: color('error', 'dark', 0.15),
    textColor: color('error', 'dark'),
  },
  processed: {
    backgroundColor: color('custom', 'chipSecondaryText', 0.15),
    textColor: color('custom', 'chipSecondaryText'),
  },
  resolved: {
    backgroundColor: color('success', 'dark', 0.15),
    textColor: color('success', 'dark'),
  },
};

export const generateDescription = (alert: Alert): string => {
  const suggestionDescription = alert.suggestionDescription || '';
  const suggestionCauses = alert.suggestionCauses ? JSON.parse(alert.suggestionCauses) : [];

  let description = suggestionDescription;

  if (Array.isArray(suggestionCauses) && suggestionCauses.length > 0) {
    description += '\n<ul>';
    suggestionCauses.forEach((cause) => {
      description += `<li>${cause.name}</li>`;
    });
    description += '</ul>';
  }

  return description;
};
